import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiPost } from "@/lib/apiClient";
import { CRM_ACCOUNT_STATUSES, CRM_LEAD_SOURCES, formatCrmStatusLabel, normalizeCrmSourceForSave } from "@/lib/crmDisplay";
import { ArrowLeft } from "lucide-react";

const ACCOUNT_STAGES = [
  "new",
  "qualified",
  "meeting_booked",
  "proposal_sent",
  "negotiation",
  "closed_won",
  "closed_lost",
] as const;

export function CrmAccountNewPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [industry, setIndustry] = useState("");
  const [source, setSource] = useState("Manual");
  const [status, setStatus] = useState<(typeof CRM_ACCOUNT_STATUSES)[number]>("prospect");
  const [stage, setStage] = useState<(typeof ACCOUNT_STAGES)[number]>("new");
  const [financeCustomer, setFinanceCustomer] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      if (!name.trim()) throw new Error("Account name is required.");
      const res = await apiPost<{ id?: string }>("/api/crm/accounts", {
        accountKind: "company",
        name: name.trim(),
        domain: domain.trim() || undefined,
        website: website.trim() || undefined,
        email: email.trim() || undefined,
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        industry: industry.trim() || undefined,
        source: normalizeCrmSourceForSave(source),
        status,
        stage,
        score: 0,
        financeCustomer,
        financeVendor: false,
      });
      if (!res.ok || !res.data?.id) throw new Error(res.error || "Failed to create account.");
      void navigate(`/crm/accounts/${encodeURIComponent(res.data.id)}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create account.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AccountNewForm
      title="New company account"
      onSubmit={handleSubmit}
      error={error}
      isSubmitting={isSubmitting}
      name={name}
      setName={setName}
      domain={domain}
      setDomain={setDomain}
      website={website}
      setWebsite={setWebsite}
      email={email}
      setEmail={setEmail}
      city={city}
      setCity={setCity}
      state={state}
      setState={setState}
      industry={industry}
      setIndustry={setIndustry}
      source={source}
      setSource={setSource}
      status={status}
      setStatus={setStatus}
      stage={stage}
      setStage={setStage}
      financeCustomer={financeCustomer}
      setFinanceCustomer={setFinanceCustomer}
    />
  );
}

export function CrmAccountNewIndividualPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      if (!name.trim()) throw new Error("Account name is required.");
      const res = await apiPost<{ id?: string }>("/api/crm/accounts/individual", {
        name: name.trim(),
        email: email.trim() || undefined,
        source: "Manual",
        status: "prospect",
        stage: "new",
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        phone: phone.trim() || undefined,
      });
      if (!res.ok || !res.data?.id) throw new Error(res.error || "Failed to create person account.");
      void navigate(`/crm/accounts/${encodeURIComponent(res.data.id)}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create account.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Link
        to="/crm/accounts"
        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All accounts
      </Link>
      <h1 className="text-2xl font-semibold">New person account</h1>
      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}
      <form onSubmit={(e) => void handleSubmit(e)} className="max-w-xl space-y-3">
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Account display name"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          />
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          />
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          />
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          />
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          {isSubmitting ? "Creating…" : "Create person account"}
        </button>
      </form>
    </div>
  );
}

function AccountNewForm(props: {
  title: string;
  onSubmit: (e: FormEvent) => void;
  error: string | null;
  isSubmitting: boolean;
  name: string;
  setName: (v: string) => void;
  domain: string;
  setDomain: (v: string) => void;
  website: string;
  setWebsite: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  city: string;
  setCity: (v: string) => void;
  state: string;
  setState: (v: string) => void;
  industry: string;
  setIndustry: (v: string) => void;
  source: string;
  setSource: (v: string) => void;
  status: string;
  setStatus: (v: (typeof CRM_ACCOUNT_STATUSES)[number]) => void;
  stage: string;
  setStage: (v: (typeof ACCOUNT_STAGES)[number]) => void;
  financeCustomer: boolean;
  setFinanceCustomer: (v: boolean) => void;
}) {
  return (
    <div className="space-y-6">
      <Link
        to="/crm/accounts"
        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All accounts
      </Link>
      <h1 className="text-2xl font-semibold">{props.title}</h1>
      {props.error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{props.error}</div>
      ) : null}
      <form onSubmit={(e) => void props.onSubmit(e)} className="max-w-xl space-y-3">
        <input
          required
          value={props.name}
          onChange={(e) => props.setName(e.target.value)}
          placeholder="Company name"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            value={props.domain}
            onChange={(e) => props.setDomain(e.target.value)}
            placeholder="Domain"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          />
          <input
            value={props.website}
            onChange={(e) => props.setWebsite(e.target.value)}
            placeholder="Website"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          />
          <input
            value={props.email}
            onChange={(e) => props.setEmail(e.target.value)}
            placeholder="Company email"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          />
          <input
            value={props.industry}
            onChange={(e) => props.setIndustry(e.target.value)}
            placeholder="Industry"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          />
          <input
            value={props.city}
            onChange={(e) => props.setCity(e.target.value)}
            placeholder="City"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          />
          <input
            value={props.state}
            onChange={(e) => props.setState(e.target.value)}
            placeholder="State"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <select
            value={props.source}
            onChange={(e) => props.setSource(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          >
            {CRM_LEAD_SOURCES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            value={props.status}
            onChange={(e) => props.setStatus(e.target.value as (typeof CRM_ACCOUNT_STATUSES)[number])}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          >
            {CRM_ACCOUNT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {formatCrmStatusLabel(s)}
              </option>
            ))}
          </select>
          <select
            value={props.stage}
            onChange={(e) => props.setStage(e.target.value as (typeof ACCOUNT_STAGES)[number])}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          >
            {ACCOUNT_STAGES.map((s) => (
              <option key={s} value={s}>
                {formatCrmStatusLabel(s)}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={props.financeCustomer}
            onChange={(e) => props.setFinanceCustomer(e.target.checked)}
          />
          Enable as Finance customer (required for deals)
        </label>
        <button
          type="submit"
          disabled={props.isSubmitting}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          {props.isSubmitting ? "Creating…" : "Create account"}
        </button>
      </form>
    </div>
  );
}
